import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReplayIcon from "@mui/icons-material/Replay";

import { Color } from "../../../../styles/SharedStyles";
import { DetailField } from "./DetailField";
import { BadgeType, MeetingInfoBadge } from "../MeetingInfoBadge";
import React from "react";
import { RecurringMetadataEditDialog } from "../metadata_editing/RecurringMetadataEditDialog";
import { getTimeString, recurrenceRuleString } from "../../helpers";
import { Meeting } from "../../../../model/meeting";
import { toDisplayString } from "../../../../model/meeting_host_schedule";
import { useBoundStore } from "../../../../store/store";
import { meetingTypeLabel } from "../../../../model/meeting_type";

interface RecurrenceDetailsViewProps {
  meeting: Meeting;
}

export const RecurrenceDetailsView: React.FC<RecurrenceDetailsViewProps> = ({
  meeting,
}) => {
  if (!meeting.recurringMeeting) {
    throw new Error("Meeting is not recurring");
  }

  const getMeetingType = useBoundStore((state) => state.getTypeById);
  const [isEditing, setIsEditing] = React.useState(false);
  const [showRecurring, setShowRecurring] = React.useState(false);

  const recMeeting = meeting.recurringMeeting!;
  const isVisible = recMeeting.metadata.memberVisible ?? false;
  const meetingTypeInfo = recMeeting.metadata.meetingType
    ? getMeetingType(recMeeting.metadata.meetingType)
    : undefined;
  const meetingType = meetingTypeInfo
    ? meetingTypeLabel(meetingTypeInfo)
    : undefined;
  const isMissingData = !meetingType || !recMeeting.metadata.hostSchedule;

  return (
    <Accordion
      expanded={showRecurring}
      onChange={() => setShowRecurring(!showRecurring)}
      sx={{ bgcolor: Color.LightestPurple, borderRadius: "10px" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Stack direction="row" gap={2} alignItems={"center"}>
          <ReplayIcon />
          <Typography variant="body2" fontWeight="bold">
            This is an instance of a recurring meeting.
          </Typography>
          <Typography variant="caption" sx={{ marginLeft: "auto" }}>
            {showRecurring ? "Hide" : "Show"} recurrence details
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2} padding={{ xs: 0, sm: "10px 24px" }}>
          <Typography fontWeight="bold">Recurrence Details</Typography>
          <Divider />
          <Stack
            gap={2}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Stack gap={1} sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight="bold">
                {meeting.title}
              </Typography>
              {(!isVisible || isMissingData) && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {!isVisible && (
                    <MeetingInfoBadge type={BadgeType.notVisible} />
                  )}
                  {isMissingData && (
                    <MeetingInfoBadge type={BadgeType.missingData} />
                  )}
                </Stack>
              )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(!isEditing)}
              sx={{ flexShrink: 0 }}
            >
              Edit Recurrence
            </Button>
          </Stack>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <DetailField field="id" value={recMeeting.id} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DetailField
                field="recurrenceRule"
                value={recurrenceRuleString(recMeeting.recurrenceRule)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DetailField
                field="time"
                value={getTimeString(meeting.start, meeting.end)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DetailField
                field="hostName"
                value={toDisplayString(recMeeting.metadata.hostSchedule)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DetailField field="meetingType" value={meetingType} />
            </Grid>
            {recMeeting.metadata.continuousRoomKey && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <DetailField
                  field="continuousRoomKey"
                  value={recMeeting.metadata.continuousRoomKey}
                />
              </Grid>
            )}
          </Grid>
          <RecurringMetadataEditDialog
            key={meeting.id}
            meeting={meeting}
            open={isEditing}
            onCancel={() => setIsEditing(false)}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
